import React, { useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    // Ждем готовности canvas
    const initCanvas = () => {
      const width = canvas?.offsetWidth || window.innerWidth;
      const height = canvas?.offsetHeight || window.innerHeight;
      
      if (width === 0 || height === 0) {
        // Canvas еще не готов, пробуем через небольшую задержку
        setTimeout(initCanvas, 50);
        return;
      }

      canvas.width = width;
      canvas.height = height;
      
      // Получаем контекст заново после установки размеров
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      const particleCount = 50;
      const connectionDistance = 150;

      class Particle {
        constructor() {
          this.x = Math.random() * canvas?.width;
          this.y = Math.random() * canvas?.height;
          this.vx = (Math.random() - 0.5) * 0.5;
          this.vy = (Math.random() - 0.5) * 0.5;
          this.radius = Math.random() * 2 + 1;
        }

        update() {
          this.x += this.vx;
          this.y += this.vy;

          if (this.x < 0 || this.x > canvas?.width) this.vx *= -1;
          if (this.y < 0 || this.y > canvas?.height) this.vy *= -1;
        }

        draw(ctx) {
          if (!ctx) return;
          ctx?.beginPath();
          ctx?.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(0, 212, 255, 0.6)';
          ctx?.fill();
        }
      }

      // Инициализация частиц
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push(new Particle());
      }

      function animate() {
        const canvas = canvasRef?.current;
        if (!canvas) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return;
        }

        // Получаем ctx заново в каждом кадре
        const ctx = canvas?.getContext('2d');
        if (!ctx) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return;
        }

        // Проверяем размеры canvas
        const currentWidth = canvas?.offsetWidth || window.innerWidth;
        const currentHeight = canvas?.offsetHeight || window.innerHeight;
        
        if (canvas.width !== currentWidth || canvas.height !== currentHeight) {
          canvas.width = currentWidth;
          canvas.height = currentHeight;
          // Пересоздаем частицы при изменении размеров
          particlesRef.current = [];
          for (let i = 0; i < particleCount; i++) {
            particlesRef.current.push(new Particle());
          }
        }

        ctx?.clearRect(0, 0, canvas?.width, canvas?.height);

        particlesRef.current?.forEach((particle, index) => {
          particle?.update();
          particle?.draw(ctx);

          for (let j = index + 1; j < particlesRef.current?.length; j++) {
            const dx = particlesRef.current?.[j]?.x - particle?.x;
            const dy = particlesRef.current?.[j]?.y - particle?.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < connectionDistance) {
              ctx?.beginPath();
              ctx.strokeStyle = `rgba(157, 78, 221, ${1 - distance / connectionDistance})`;
              ctx.lineWidth = 0.5;
              ctx?.moveTo(particle?.x, particle?.y);
              ctx?.lineTo(particlesRef.current?.[j]?.x, particlesRef.current?.[j]?.y);
              ctx?.stroke();
            }
          }
        });

        animationFrameRef.current = requestAnimationFrame(animate);
      }

      // Запуск анимации
      animate();

      const handleResize = () => {
        const canvas = canvasRef?.current;
        if (!canvas) return;
        const width = canvas?.offsetWidth || window.innerWidth;
        const height = canvas?.offsetHeight || window.innerHeight;
        
        if (width > 0 && height > 0) {
          canvas.width = width;
          canvas.height = height;
          
          // Пересоздаем частицы с новыми размерами
          particlesRef.current = [];
          for (let i = 0; i < particleCount; i++) {
            particlesRef.current.push(new Particle());
          }
        }
      };

      window.addEventListener('resize', handleResize);
      
      // Cleanup функция
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        window.removeEventListener('resize', handleResize);
        particlesRef.current = [];
      };
    };

    // Запускаем инициализацию
    initCanvas();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#9D4EDD] via-[#00D4FF] to-[#0A0E27]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      />

      <div className="absolute inset-0 bg-[#0A0E27] opacity-70" />

      <div className="relative z-10 container mx-auto px-4 py-20 lg:py-32">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1E293B] rounded-full px-6 py-3 mb-8 animate-pulse">
            <Icon name="Sparkles" size={20} color="#00D4FF" />
            <span className="text-sm font-medium text-[#00D4FF]">
              Работает на передовых AI технологиях
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-headline font-bold text-white mb-6 leading-tight">
            Интеллект, который работает
            <br />
            <span className="bg-gradient-to-r from-[#00D4FF] to-[#9D4EDD] bg-clip-text text-transparent">
              пока вы спите
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            Внедряем ИИ в ваш бизнес
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              variant="default"
              size="lg"
              iconName="Rocket"
              iconPosition="right"
              onClick={() => window.open('https://wa.me/77476660065', '_blank')}
              className="w-full sm:w-auto shadow-[0_0_15px_rgba(255,255,255,0.3)] border border-white hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]"
            >
              Начать бесплатно
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Play"
              iconPosition="left"
              onClick={() => window.location.href = '/demo-playground#demo-chat'}
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#0A0E27] shadow-[0_0_15px_rgba(255,255,255,0.3)] hover:shadow-[0_0_25px_rgba(255,255,255,0.5)] transition-all duration-300"
            >
              Смотреть демо
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Icon name="Zap" size={24} color="#FFFFFF" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">98.5%</h3>
              <p className="text-gray-300 text-sm">Уровень удовлетворенности клиентов</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Icon name="MessageSquare" size={24} color="#FFFFFF" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">2.5M+</h3>
              <p className="text-gray-300 text-sm">Сообщений обрабатывается ежедневно</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Icon name="Clock" size={24} color="#FFFFFF" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">15,000+</h3>
              <p className="text-gray-300 text-sm">Часов автоматизации сэкономлено</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={32} color="#FFFFFF" className="opacity-50" />
      </div>
    </section>
  );
};

export default HeroSection;